// Per-Assignment credits subsection. Renders Sales + Managed lists and
// running totals; supports add / replace / delete via lifted modal
// state. Credits data is sourced from the page's eager-parallel map
// (frontend-design F-002).

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  creditsApi,
  peopleApi,
} from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { canEditCreditsOn } from '@/auth/permissions';
import { formatPercentInt } from '@/utils/format';
import { ApiError } from '@/types/errors';
import type { Agreement, Assignment, Credit, Person, Principal } from '@/types/domain';
import { useToast } from './ToastHost';
import { ConfirmationDialog } from './ConfirmationDialog';
import { CreditEditor } from '@/forms/CreditEditor';

interface Props {
  assignment: Assignment;
  agreement: Agreement;
  principal: Principal | null;
  credits: Credit[] | null;
  loading: boolean;
  error: ApiError | null;
  onCreditsChange: () => void;
}

export function CreditsSubsection({
  assignment,
  agreement,
  principal,
  credits,
  loading,
  error,
  onCreditsChange,
}: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const editGate = canEditCreditsOn(principal, agreement);
  const showAdd = editGate.rendering === 'visible';

  const peopleQ = useQuery({
    queryKey: qk.peopleAll(),
    queryFn: () => peopleApi.list({ limit: 100 }),
    staleTime: 60_000,
  });
  const peopleById = useMemo(() => {
    const m = new Map<string, Person>();
    for (const p of peopleQ.data?.data ?? []) m.set(p.id, p);
    return m;
  }, [peopleQ.data]);

  const sales = (credits ?? []).filter((c) => c.credit_type === 'sales');
  const managed = (credits ?? []).filter((c) => c.credit_type === 'managed');
  const salesTotal = sales.reduce((s, c) => s + c.percentage, 0);
  const managedTotal = managed.reduce((s, c) => s + c.percentage, 0);

  const [editorState, setEditorState] = useState<
    | { mode: 'create-sales' }
    | { mode: 'create-managed' }
    | { mode: 'edit'; credit: Credit }
    | { mode: 'replace'; credit: Credit }
    | null
  >(null);
  const [deleting, setDeleting] = useState<Credit | null>(null);

  const deleteM = useMutation({
    mutationFn: (id: string) => creditsApi.delete(id),
  });

  const performDelete = async () => {
    if (!deleting) return;
    try {
      await deleteM.mutateAsync(deleting.id);
      void qc.invalidateQueries({
        queryKey: qk.assignmentCredits(assignment.id),
      });
      toast.show({ severity: 'success', message: 'Credit deleted.' });
      onCreditsChange();
      setDeleting(null);
    } catch (err) {
      if (err instanceof ApiError)
        toast.show({ severity: 'error', message: err.message });
      else
        toast.show({
          severity: 'error',
          message: "Couldn't delete the credit.",
        });
      setDeleting(null);
    }
  };

  if (error) {
    return (
      <div className="inline-error" role="alert">
        Couldn't load credits.{' '}
        <button
          type="button"
          className="btn-link"
          onClick={() =>
            void qc.invalidateQueries({
              queryKey: qk.assignmentCredits(assignment.id),
            })
          }
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ paddingLeft: 16 }}>
      <h3>Credits</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <h4>
            Sales credits ({salesTotal === 100 ? '✓ 100%' : `${salesTotal}%${
              agreement.status === 'draft' ? ' — need 100% to activate' : ''
            }`})
          </h4>
          {loading ? (
            <div className="muted">Loading…</div>
          ) : sales.length === 0 ? (
            <p className="muted">No sales credits yet.</p>
          ) : (
            <ul>
              {sales.map((c) => (
                <li key={c.id} style={{ marginBottom: 4 }}>
                  {peopleById.get(c.person_id)?.name ?? c.person_id}:{' '}
                  {formatPercentInt(c.percentage)}{' '}
                  {showAdd ? (
                    <>
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() =>
                          setEditorState({ mode: 'edit', credit: c })
                        }
                      >
                        Edit %
                      </button>{' '}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() =>
                          setEditorState({ mode: 'replace', credit: c })
                        }
                      >
                        Replace person
                      </button>{' '}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => setDeleting(c)}
                        style={{ color: 'var(--c-danger)' }}
                      >
                        Delete
                      </button>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {showAdd ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditorState({ mode: 'create-sales' })}
            >
              Add sales credit
            </button>
          ) : null}
        </div>

        <div>
          <h4>Managed credits ({managedTotal}% / 100% allowed)</h4>
          {loading ? (
            <div className="muted">Loading…</div>
          ) : managed.length === 0 ? (
            <p className="muted">No managed credits yet (optional).</p>
          ) : (
            <ul>
              {managed.map((c) => (
                <li key={c.id} style={{ marginBottom: 4 }}>
                  {peopleById.get(c.person_id)?.name ?? c.person_id}:{' '}
                  {formatPercentInt(c.percentage)}{' '}
                  {showAdd ? (
                    <>
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() =>
                          setEditorState({ mode: 'edit', credit: c })
                        }
                      >
                        Edit %
                      </button>{' '}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() =>
                          setEditorState({ mode: 'replace', credit: c })
                        }
                      >
                        Replace person
                      </button>{' '}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => setDeleting(c)}
                        style={{ color: 'var(--c-danger)' }}
                      >
                        Delete
                      </button>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {showAdd ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditorState({ mode: 'create-managed' })}
            >
              Add managed credit
            </button>
          ) : null}
        </div>
      </div>

      {editorState ? (
        <CreditEditor
          open={true}
          onClose={() => setEditorState(null)}
          assignment={assignment}
          existing={
            editorState.mode === 'edit' || editorState.mode === 'replace'
              ? editorState.credit
              : null
          }
          mode={
            editorState.mode === 'edit'
              ? 'patch'
              : editorState.mode === 'replace'
                ? 'replace'
                : 'create'
          }
          forcedCreditType={
            editorState.mode === 'create-sales'
              ? 'sales'
              : editorState.mode === 'create-managed'
                ? 'managed'
                : undefined
          }
          onChanged={onCreditsChange}
        />
      ) : null}

      {deleting ? (
        <ConfirmationDialog
          open={true}
          title="Delete this credit?"
          body={`Remove ${peopleById.get(deleting.person_id)?.name ?? deleting.person_id}'s ${
            deleting.credit_type
          } credit (${deleting.percentage}%)?`}
          confirmLabel="Delete credit"
          confirmIntent="destructive"
          onConfirm={performDelete}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
}
